import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Archive,
  CalendarRange,
  CheckCircle2,
  Inbox,
  Lock,
  Printer,
  Undo2,
} from 'lucide-react';
import { api, type Pagina } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import type { CierreDeCaja, PreviaDelCierre } from '@/shared/api/tipos';
import { CargandoEnLinea } from '@/shared/ui/Cargando';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import { VistaPreviaDeImpresion } from '@/shared/ui/VistaPreviaDeImpresion';
import { notificar } from '@/shared/ui/avisos';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { Pestanas } from '@/shared/ui/Pestanas';
import { CierresDelSistemaAnterior } from './CierresDelSistemaAnterior';
import { ModalDeCierre } from './ModalDeCierre';
import { enFecha, enMomento, hace, hoy } from './fechas';

export function PaginaCierreDeCaja() {
  const { puede } = useSesion();
  const clienteDeConsultas = useQueryClient();

  const revisa = puede('caja.revisar');
  const cierraCaja = puede('caja.cerrar');

  const [desde, setDesde] = useState(hace(29));
  const [hasta, setHasta] = useState(hoy());
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(25);
  const [cerrando, setCerrando] = useState(false);
  const [imprimiendo, setImprimiendo] = useState<number | null>(null);
  const [comprobante, setComprobante] = useState<Blob | null>(null);
  const [vista, setVista] = useState<'cierres' | 'anterior'>('cierres');

  const previa = useQuery({
    queryKey: ['cierre-previa'],
    queryFn: () => api.get<PreviaDelCierre>('/cierres/previa'),

    enabled: cierraCaja,
  });

  const cierres = useQuery({
    queryKey: ['cierres', revisa, desde, hasta, pagina, tamanio],
    queryFn: () =>
      api.get<Pagina<CierreDeCaja>>(revisa ? '/cierres' : '/cierres/mios', {
        query: { desde, hasta, pagina, tamanio },
      }),
  });

  const p = previa.data;

  async function imprimir(id: number) {
    setImprimiendo(id);
    try {
      const ruta = revisa
        ? `/cierres/${id}/comprobante.pdf`
        : `/cierres/mios/${id}/comprobante.pdf`;
      const fichero = await api.descargar(ruta);
      if (!fichero) {
        notificar.error('NO SE PUDO GENERAR EL COMPROBANTE');
        return;
      }
      setComprobante(fichero);
    } catch (causa) {
      notificar.error(
        causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO ABRIR EL COMPROBANTE',
      );
    } finally {
      setImprimiendo(null);
    }
  }

  const columnas: Columna<CierreDeCaja>[] = [
    {
      clave: 'fecha',
      encabezado: 'Fecha',
      contenido: (c) => (
        <span className="block min-w-0">
          <span className="importe block text-xs font-semibold text-armazon-800">
            {enFecha(c.fecha)}
          </span>
          <span className="block truncate text-[10px] uppercase text-armazon-400">
            Cerrada {enMomento(c.cerradoEn)}
          </span>
        </span>
      ),
    },
    {
      clave: 'cajero',
      encabezado: 'Cajero',
      contenido: (c) => (
        <span className="block min-w-0">
          <span className="block truncate text-xs font-medium uppercase text-armazon-700">
            {c.nombreDelCajero}
          </span>
          <span className="block text-[10px] uppercase text-armazon-400">
            {c.caja ? `Caja ${c.caja}` : 'Sin caja asignada'}
          </span>
        </span>
      ),
    },
    {
      clave: 'cobros',
      encabezado: 'Cobros',
      alinear: 'centro',
      contenido: (c) => (
        <span className="block">
          <span className="importe text-xs font-semibold text-armazon-700">{c.cuantosCobros}</span>

          {c.cuantosAnulados > 0 && (
            <span
              className="mt-0.5 flex items-center justify-center gap-1 text-[10px] uppercase text-amber-700"
              title={`Anulado durante la jornada: $${c.anuladoTotal.toFixed(2)}`}
            >
              <Undo2 className="size-3" aria-hidden />
              {c.cuantosAnulados}
            </span>
          )}
        </span>
      ),
    },
    {
      clave: 'esperado',
      encabezado: 'Esperado',
      alinear: 'derecha',
      contenido: (c) => <Importe valor={c.esperadoEfectivo} />,
    },
    {
      clave: 'contado',
      encabezado: 'Contado',
      alinear: 'derecha',
      contenido: (c) => <Importe valor={c.contadoEfectivo} />,
    },
    {
      clave: 'fondo',
      encabezado: 'Fondo',
      alinear: 'derecha',
      contenido: (c) => <Importe valor={c.fondoDeCaja} tenue />,
    },
    {
      clave: 'diferencia',
      encabezado: 'Diferencia',
      alinear: 'derecha',
      contenido: (c) => <Descuadre cierre={c} />,
    },
    {
      clave: 'acciones',
      encabezado: '',
      alinear: 'derecha',
      clase: 'w-12',
      contenido: (c) => (
        <span className="flex justify-end">
          <button
            type="button"
            onClick={() => void imprimir(c.id)}
            disabled={imprimiendo === c.id}
            title="Imprimir el comprobante"
            aria-label="Imprimir el comprobante"
            className="grid size-8 place-items-center rounded-lg text-armazon-400 transition-colors hover:bg-armazon-100 hover:text-armazon-700 disabled:opacity-50"
          >
            <Printer className="size-4" aria-hidden />
          </button>
        </span>
      ),
    },
  ];

  return (
    <>
      <EncabezadoDePagina
        icono={Lock}
        color="pizarra"
        titulo="Cierre de caja"
        subtitulo="Contar el cajón y dejar constancia de lo que había"
        acciones={
          <BotonDeHoy
            previa={p}
            alCerrar={() => setCerrando(true)}
            alImprimir={() => p?.yaCerrada && void imprimir(p.yaCerrada.id)}
          />
        }
      />

      {revisa && (
        <Pestanas
          etiqueta="Qué cierres"
          activa={vista}
          alElegir={setVista}
          pestanas={[
            { clave: 'cierres', rotulo: 'Cierres', descripcion: 'Los de SIREPAGAL', icono: Lock },
            {
              clave: 'anterior',
              rotulo: 'Sistema anterior',
              descripcion: 'Reconstruidos, solo consulta',
              icono: Archive,
            },
          ]}
        />
      )}

      {vista === 'anterior' ? (
        <CierresDelSistemaAnterior
          desde={desde}
          hasta={hasta}
          herramientasDeFecha={
            <Fechas
              desde={desde}
              hasta={hasta}
              alCambiar={(d, h) => {
                setDesde(d);
                setHasta(h);
              }}
            />
          }
        />
      ) : (
        <>
          {cierraCaja && <EstadoDeHoy previa={p} cargando={previa.isPending} />}

          <Tabla
            datos={cierres.data}
            cargando={cierres.isPending}

            columnas={revisa ? columnas : columnas.filter((c) => c.clave !== 'cajero')}
            tamanio={tamanio}
            alCambiarPagina={setPagina}
            alCambiarTamanio={(t) => {
              setTamanio(t);
              setPagina(0);
            }}
            entidad="cierres"
            iconoVacio={Lock}
            mensajeVacio={
              revisa ? 'No hay cierres en esas fechas' : 'Todavía no ha cerrado ninguna caja'
            }
            herramientas={
              <Fechas
                desde={desde}
                hasta={hasta}
                alCambiar={(d, h) => {
                  setDesde(d);
                  setHasta(h);
                  setPagina(0);
                }}
              />
            }
          />
        </>
      )}

      {cerrando && p && (
        <ModalDeCierre
          previa={p}
          alCancelar={() => setCerrando(false)}
          alCerrado={(cierre) => {
            setCerrando(false);
            void clienteDeConsultas.invalidateQueries({ queryKey: ['cierres'] });

            void imprimir(cierre.id);
          }}
        />
      )}

      <VistaPreviaDeImpresion
        abierto={imprimiendo !== null || comprobante !== null}
        alCerrar={() => setComprobante(null)}
        titulo="Comprobante del cierre"
        subtitulo="Arqueo de caja"
        fichero={comprobante}
        cargando={imprimiendo !== null}
        formato="hoja"
        textoDeCarga="Preparando el comprobante del cierre…"
      />
    </>
  );
}

function BotonDeHoy({
  previa,
  alCerrar,
  alImprimir,
}: {
  previa: PreviaDelCierre | undefined;
  alCerrar: () => void;
  alImprimir: () => void;
}) {
  if (!previa) {
    return null;
  }

  if (previa.yaCerrada) {
    return (
      <button
        type="button"
        onClick={alImprimir}
        title="Imprimir el comprobante de hoy"
        aria-label="Imprimir el comprobante de hoy"
        className="grid size-11 place-items-center rounded-xl border border-armazon-200 bg-white text-armazon-500 transition-colors hover:border-marca-300 hover:text-marca-700"
      >
        <Printer className="size-5" aria-hidden />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={alCerrar}
      title="Cerrar la caja"
      aria-label="Cerrar la caja"
      className="grid size-11 place-items-center rounded-xl bg-marca-600 text-white shadow-sm transition-colors hover:bg-marca-700"
    >
      <Lock className="size-5" aria-hidden />
    </button>
  );
}

function EstadoDeHoy({
  previa,
  cargando,
}: {
  previa: PreviaDelCierre | undefined;
  cargando: boolean;
}) {
  if (cargando) {
    return (
      <div className="mb-4 rounded-2xl border border-armazon-200 bg-white">
        <CargandoEnLinea detalle="Sumando la jornada" />
      </div>
    );
  }
  if (!previa) {
    return null;
  }

  const c = previa.yaCerrada;

  if (c) {
    const cuadra = c.diferencia === 0;
    return (
      <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-3 rounded-2xl border border-armazon-200 bg-white p-4 shadow-sm">
        <span className="flex items-center gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-armazon-100 text-armazon-600">
            <Lock className="size-5" aria-hidden />
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-bold uppercase text-armazon-800">
              Su caja de hoy ya está cerrada
            </span>
            <span className="block text-[11px] uppercase text-armazon-500">
              {c.nombreDelCajero}
              {c.caja && ` · Caja ${c.caja}`} · {enMomento(c.cerradoEn)}
            </span>
          </span>
        </span>

        <span className="ml-auto flex flex-wrap items-center gap-x-6 gap-y-2">
          <Cifra rotulo="Esperado" valor={c.esperadoEfectivo} />
          <Cifra rotulo="Contado" valor={c.contadoEfectivo} />
          <Cifra rotulo="Fondo" valor={c.fondoDeCaja} />
          <span className="text-right">
            <span
              className={`flex items-center justify-end gap-1 text-[10px] font-semibold uppercase tracking-wide ${
                cuadra ? 'text-marca-700' : 'text-red-700'
              }`}
            >
              {cuadra ? (
                <CheckCircle2 className="size-3.5" aria-hidden />
              ) : (
                <AlertCircle className="size-3.5" aria-hidden />
              )}
              {cuadra ? 'Cuadró' : c.diferencia > 0 ? 'Sobró' : 'Faltó'}
            </span>
            <span
              className={`importe block text-lg font-bold ${
                cuadra ? 'text-marca-700' : 'text-red-700'
              }`}
            >
              ${Math.abs(c.diferencia).toFixed(2)}
            </span>
          </span>
        </span>
      </div>
    );
  }

  return (
    <div className="relative mb-4 flex flex-wrap items-center gap-x-8 gap-y-3 overflow-hidden rounded-2xl bg-armazon-900 p-5 text-white shadow-sm">
      <span className="min-w-0">
        <span className="block text-[10px] font-semibold uppercase tracking-wide text-white/60">
          Debe haber en el cajón
        </span>
        <span className="importe block text-3xl font-bold">
          ${previa.esperadoEfectivo.toFixed(2)}
        </span>
        <span className="block text-[11px] uppercase text-white/60">
          {previa.cuantosCobros} {previa.cuantosCobros === 1 ? 'cobro' : 'cobros'} en efectivo
          {previa.caja && ` · Caja ${previa.caja}`}
        </span>
      </span>

      {previa.esperadoOtros > 0 && (
        <span className="min-w-0">
          <span className="block text-[10px] font-semibold uppercase tracking-wide text-white/60">
            Fuera del cajón
          </span>
          <span className="importe block text-xl font-bold text-white/90">
            ${previa.esperadoOtros.toFixed(2)}
          </span>
          <span className="block text-[11px] uppercase text-white/60">
            Se cuadra contra el banco
          </span>
        </span>
      )}

      {previa.cuantosAnulados > 0 && (
        <span className="min-w-0">
          <span className="block text-[10px] font-semibold uppercase tracking-wide text-amber-300/80">
            Anulado en la jornada
          </span>
          <span className="importe block text-xl font-bold text-amber-200">
            ${previa.anuladoTotal.toFixed(2)}
          </span>
          <span className="block text-[11px] uppercase text-white/60">
            Ya descontado de lo que debe haber
          </span>
        </span>
      )}

      <Inbox className="absolute -bottom-3 right-4 size-20 text-white/5" aria-hidden />
    </div>
  );
}

function Fechas({
  desde,
  hasta,
  alCambiar,
}: {
  desde: string;
  hasta: string;
  alCambiar: (desde: string, hasta: string) => void;
}) {
  return (
    <span className="flex items-center gap-2">
      <CalendarRange className="size-4 shrink-0 text-armazon-400" aria-hidden />
      <input
        type="date"
        value={desde}
        max={hasta}
        onChange={(evento) => alCambiar(evento.target.value, hasta)}
        aria-label="Desde"
        className="importe h-9 rounded-lg border border-armazon-200 px-2 text-xs outline-none transition-colors focus:border-marca-400"
      />
      <span className="text-[10px] uppercase text-armazon-400">a</span>
      <input
        type="date"
        value={hasta}
        min={desde}
        onChange={(evento) => alCambiar(desde, evento.target.value)}
        aria-label="Hasta"
        className="importe h-9 rounded-lg border border-armazon-200 px-2 text-xs outline-none transition-colors focus:border-marca-400"
      />
    </span>
  );
}

function Importe({ valor, tenue }: { valor: number; tenue?: boolean }) {
  return (
    <span className={`importe text-xs ${tenue ? 'text-armazon-400' : 'text-armazon-700'}`}>
      ${valor.toFixed(2)}
    </span>
  );
}

function Descuadre({ cierre }: { cierre: CierreDeCaja }) {
  if (cierre.diferencia === 0) {
    return <span className="importe text-xs text-armazon-400">$0.00</span>;
  }

  const sobra = cierre.diferencia > 0;

  return (
    <span
      title={cierre.motivoDiferencia ?? undefined}
      className={`importe inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-bold ${
        sobra ? 'bg-blue-50 text-blue-800' : 'bg-red-50 text-red-800'
      }`}
    >
      {sobra ? '+' : '−'}${Math.abs(cierre.diferencia).toFixed(2)}
    </span>
  );
}

function Cifra({ rotulo, valor }: { rotulo: string; valor: number }) {
  return (
    <span className="text-right">
      <span className="block text-[10px] font-semibold uppercase tracking-wide text-armazon-500">
        {rotulo}
      </span>
      <span className="importe block text-lg font-bold text-armazon-900">${valor.toFixed(2)}</span>
    </span>
  );
}
